import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Tabs, Tab, Divider } from "@mui/material";
import Jobs from "./Jobs"; // Assuming the Jobs component is in the same directory
import IntrestedJobs from "./InterestedJobs";
import { myCandidatesSelector } from "../redux/selector";
import { useDispatch, useSelector } from "react-redux";
import Submissions from "./Submissions";
import { selectedResume, selectedSubmissionAction, selectedTabViewAction, setActiveCandidateTab } from "../redux/action";
import { useLocation } from "react-router-dom";


const CandidatesTabs = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { candidateDetails, interested_jobs_daycount, jobs_daycount, tabView, activeCandidates, refreshKey } =
    useSelector(myCandidatesSelector);

  // Check sessionStorage on mount — set by ZohoCallback and survives the activeCandidatesLoading gate
  const isFromZohoJobs = useRef(!!sessionStorage.getItem("zoho_redirect_jobs"));
  const isFromZoho = useRef(!!sessionStorage.getItem("zoho_redirect"));
  const initialTab = isFromZohoJobs.current
    ? 0
    : isFromZoho.current
      ? 2
      : location.state?.tabView === "Submission"
        ? 2
        : location.state?.tabView === "Interested"
          ? 1
          : 0;
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [hasInitialized, setHasInitialized] = useState(false);
  const prevTabRef = useRef(selectedTab);

  useEffect(() => {
    if (prevTabRef.current === 2 && selectedTab !== 2) {
      dispatch(selectedSubmissionAction(null));
      sessionStorage.removeItem("target_job_id");
    }
    prevTabRef.current = selectedTab;
  }, [selectedTab, dispatch]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    dispatch(selectedResume({}));

    if (newValue === 2) {
      dispatch(selectedSubmissionAction(null));
    } else {
      // Leaving submissions — clear previous selection so it doesn't stick
      dispatch(selectedSubmissionAction(null));
      sessionStorage.removeItem("target_job_id");
    }

    const tabDetailView =
      newValue === 0 ? "Jobs" : newValue === 1 ? "Interested" : "Submission";
    dispatch(selectedTabViewAction(tabDetailView));
    dispatch(setActiveCandidateTab(newValue));
  };

  // Handle initial navigation - only run once
  useEffect(() => {
    if (!hasInitialized) {
      if (isFromZohoJobs.current) {
        setSelectedTab(0);
        dispatch(selectedTabViewAction("Jobs"));
        dispatch(setActiveCandidateTab(0));
        isFromZohoJobs.current = false;
      } else if (isFromZoho.current) {
        // Coming back from Zoho OAuth — force Submissions tab and clear the flag
        setSelectedTab(2);
        dispatch(selectedTabViewAction("Submission"));
        dispatch(setActiveCandidateTab(2));
        sessionStorage.removeItem("zoho_redirect"); // Clear flag so future navigations aren't affected
        isFromZoho.current = false;
      } else if (location.state?.tabView === "Submission") {
        // Only open Submissions when intentionally navigated (e.g. dashboard click)
        setSelectedTab(2);
        dispatch(selectedTabViewAction("Submission"));
        dispatch(setActiveCandidateTab(2));
      } else if (location.state?.tabView === "Interested" || tabView === "Interested") {
        setSelectedTab(1);
        dispatch(selectedTabViewAction("Interested"));
        dispatch(setActiveCandidateTab(1));
      } else {
        setSelectedTab(0);
        dispatch(selectedTabViewAction("Jobs"));
        dispatch(setActiveCandidateTab(0));
      }
      setHasInitialized(true);
    }
  }, [location.state, hasInitialized, dispatch, tabView]);

  // Handle subsequent tab view changes from Redux (but not initial navigation)
  useEffect(() => {
    if (hasInitialized) {
      if (tabView === "Submission") {
        setSelectedTab(2);
        dispatch(setActiveCandidateTab(2));
      } else if (tabView === "Jobs") {
        setSelectedTab(0);
        dispatch(setActiveCandidateTab(0));
      } else if (tabView === "Interested") {
        setSelectedTab(1);
        dispatch(setActiveCandidateTab(1));
      }
    }
  }, [tabView, hasInitialized]);

  const paperStyles = {
    borderRadius: "8px",
    height: "calc(100vh - 160px)", // Matches ActiveCandidates height exactly
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <Box>
      <Paper elevation={2} sx={paperStyles}>
        {/* Tab navigation */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ bgcolor: "#fff", borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <span>Jobs</span>{" "}
                <Box component="span" ml={0.5}>
                  {jobs_daycount > 0 ? `(${jobs_daycount})` : ("")}
                </Box>
              </Box>
            }
            style={{ textTransform: "none" }}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <span>Interested</span>{" "}
                <Box component="span" ml={0.5}>
                  {interested_jobs_daycount > 0 ? `(${interested_jobs_daycount})` : ("")}
                </Box>
              </Box>
            }
            style={{ textTransform: "none" }}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <span>Submissions</span>
              </Box>
            }
            style={{ textTransform: "none" }}
          />
        </Tabs>
        <Divider />
        {/* Render the correct component based on the selected tab */}
        {selectedTab === 0 && <Jobs refreshKey={refreshKey} />} {/* Jobs Tab */}
        {selectedTab === 1 && <IntrestedJobs refreshKey={refreshKey} />} {/* Interested Tab */}
        {selectedTab === 2 && <Submissions refreshKey={refreshKey} />} {/* Submissions Tab */}
      </Paper>
    </Box >
  );
};

export default CandidatesTabs;
